import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { loadIndex, search, type SearchDoc, type SearchHit } from '@/lib/search'

/** Popular entry points shown before the visitor types anything. */
const SUGGESTED = ['CoQ10', 'Dosage', 'Buying Guide', 'Comparison', 'Side Effects', 'Third-Party Testing']

export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [docs, setDocs] = useState<SearchDoc[]>([])
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (open) {
      loadIndex().then(setDocs)
      setTimeout(() => inputRef.current?.focus(), 30)
    } else {
      setQ('')
      setActive(0)
    }
  }, [open])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const hits: SearchHit[] = q.trim() ? search(docs, q, 8) : []

  function go(hit: SearchHit) {
    onClose()
    navigate(`/articles/${hit.doc.slug}`)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') return onClose()
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, hits.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && hits[active]) {
      e.preventDefault()
      go(hits[active])
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-void/80 px-4 pt-[12vh] backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Search articles"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl border border-line-strong bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-line px-5">
          <span aria-hidden className="text-faint">
            ⌕
          </span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setActive(0)
            }}
            onKeyDown={onKeyDown}
            placeholder="Search compounds, dosing, side effects…"
            aria-label="Search articles"
            className="h-14 w-full bg-transparent text-mist outline-none placeholder:text-faint"
          />
          <button type="button" onClick={onClose} aria-label="Close search" className="text-sm text-faint hover:text-mist">
            esc
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {!q.trim() && (
            <div className="p-5">
              <p className="eyebrow">Popular</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setQ(s)}
                    className="rounded-full border border-line-strong px-3 py-1.5 text-sm text-fog transition-colors hover:border-energy hover:text-energy"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {q.trim() && hits.length === 0 && (
            <p className="p-8 text-center text-sm text-fog">
              Nothing matched “{q}”. Try a compound name, or a term like dosage or side effects.
            </p>
          )}

          {hits.map((hit, i) => (
            <button
              key={hit.doc.slug}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => go(hit)}
              className={`block w-full border-b border-line px-5 py-4 text-left last:border-b-0 transition-colors ${
                i === active ? 'bg-surface-2' : ''
              }`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-mist">{hit.doc.title}</span>
                <span className="shrink-0 text-[11px] uppercase tracking-wide text-faint">{hit.doc.category}</span>
              </div>
              <p className="mt-1 line-clamp-2 text-sm text-fog">{hit.doc.summary}</p>
              {hit.matchedTags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {hit.matchedTags.slice(0, 4).map((t) => (
                    <span key={t} className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-energy">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Opens the dialog, and wires the ⌘K / Ctrl-K shortcut. */
export function useSearchHotkey(onOpen: () => void) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpen()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onOpen])
}
